import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, AlertTriangle, LogOut, ArrowLeft } from "lucide-react";
import { api, formatISK, formatDate, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function Row({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="flex justify-between py-3 border-b border-white/10">
      <span className="text-xs uppercase tracking-[0.15em] text-gray-500">{label}</span>
      <span className="text-sm text-white text-right">{value}</span>
    </div>
  );
}

export default function StaffCheckin() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [booking, setBooking] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/bookings/${bookingId}`).then((r) => setBooking(r.data.booking)).catch(() => setErr("Booking not found"));
  }, [bookingId]);

  const doCheckin = async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.post(`/staff/checkin/${bookingId}`);
      setResult({ checked_in: r.data.booking.checked_in, already: r.data.already_checked_in });
      setBooking(r.data.booking);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Check-in failed");
    } finally {
      setLoading(false);
    }
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const diets = booking?.diets ? Object.entries(booking.diets).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${k}`).join(" · ") : "";

  return (
    <div data-testid="staff-checkin-page" className="min-h-screen bg-background grain">
      <header className="border-b border-white/10 bg-[#050505]/70 backdrop-blur-xl">
        <div className="max-w-2xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/admin" className="text-xs uppercase tracking-[0.2em] text-gray-400 hover:text-white inline-flex items-center gap-2"><ArrowLeft size={14} /> Dashboard</Link>
          <span className="font-display font-extrabold text-lg uppercase tracking-tighter">Staff Check-in</span>
          <button data-testid="staff-logout" onClick={doLogout} className="text-xs uppercase tracking-[0.2em] text-gray-400 hover:text-white inline-flex items-center gap-2"><LogOut size={14} /> Logout</button>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-12">
        {err && !booking && <p className="text-[#C44536] text-sm uppercase tracking-[0.15em]">{err}</p>}
        {booking && (
          <>
            {/* status banner */}
            {(result || booking.checked_in) && (
              <div data-testid="checkin-status" className={`mb-8 p-6 border ${result?.already || (booking.checked_in && !result) ? "border-[#C4a536]/40 bg-[#2a2410]" : "border-emerald-500/40 bg-emerald-950/30"}`}>
                <div className="flex items-center gap-3">
                  {result?.already ? <AlertTriangle className="text-yellow-500" size={22} /> : <CheckCircle2 className="text-emerald-400" size={22} />}
                  <div>
                    <p className="font-display font-bold uppercase tracking-tight text-lg">
                      {result?.already ? "Already Checked In" : "Checked In"}
                    </p>
                    {booking.checked_in_at && <p className="text-xs text-gray-400 mt-1">{new Date(booking.checked_in_at).toLocaleString()}</p>}
                  </div>
                </div>
              </div>
            )}

            <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-3">Ref {booking.id.slice(0, 8).toUpperCase()}</p>
            <h1 className="font-display font-extrabold uppercase tracking-tighter text-3xl sm:text-4xl leading-[0.95] mb-6">
              {booking.type === "dinner" ? `Dinner — ${booking.seating_label || ""} ${booking.seating}` : booking.tour_name}
            </h1>

            <div className="bg-[#0C0C0E] border border-white/10 p-6 mb-8">
              <Row label="Guest" value={booking.customer.full_name} />
              <Row label="Email" value={booking.customer.email} />
              <Row label="Phone" value={booking.customer.phone} />
              <Row label="Date" value={formatDate(booking.date)} />
              {booking.type === "dinner" && <Row label="Sitting" value={`${booking.seating_label || ""} ${booking.seating}`} />}
              {booking.type === "dinner" && <Row label="Party" value={booking.party_size} />}
              {booking.type === "dinner" && <Row label="Menu" value={diets} />}
              {booking.type === "dinner" && <Row label="Allergies" value={booking.customer.allergies} />}
              {booking.type === "buggy" && <Row label="Time" value={booking.slot} />}
              {booking.type === "buggy" && <Row label="Setup" value={`${booking.rider_type} rider`} />}
              {booking.type === "buggy" && <Row label="Buggies" value={booking.buggies} />}
              {(booking.type === "tour") && <Row label="Guests" value={booking.adults} />}
              <Row label="Total paid" value={formatISK(booking.total)} />
            </div>

            <button
              data-testid="staff-checkin-btn"
              onClick={doCheckin}
              disabled={loading || booking.checked_in}
              className="w-full bg-white text-black hover:bg-gray-200 uppercase tracking-[0.15em] text-sm font-bold py-5 transition-colors inline-flex items-center justify-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {loading ? <><Loader2 size={18} className="animate-spin" /> Checking in</> : booking.checked_in ? "Already Checked In" : "Confirm Check-in"}
            </button>
            {err && <p className="text-[#C44536] text-xs uppercase tracking-[0.15em] mt-4">{err}</p>}
          </>
        )}
      </div>
    </div>
  );
}
